/**
 * Strips nested spans inside Quill merge field blots, preserving only the
 * outer span with its data-value attribute. Prevents accumulation of nesting
 * when dangerouslyPasteHTML is called on already-rendered blot HTML.
 */
export const normalizeMergeFieldBlots = (html: string): string => {
    if (typeof document === 'undefined') return html;
    const doc = new DOMParser().parseFromString(
        `<body>${html}</body>`,
        'text/html',
    );
    doc.querySelectorAll<HTMLElement>('span.ql-merge-field').forEach((span) => {
        const value =
            span.getAttribute('data-value') ?? span.textContent?.trim() ?? '';
        span.textContent = value;
        Array.from(span.attributes)
            .filter((a) => !['class', 'data-value'].includes(a.name))
            .forEach((a) => span.removeAttribute(a.name));
    });
    return doc.body.innerHTML;
};

export const isEmptyHtml = (html: string): boolean =>
    !html ||
    html === '<p><br></p>' ||
    html.replace(/<(.|\n)*?>/g, '').trim() === '';

const normalizeForComparison = (text: string): string =>
    text
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

export const containsSignature = (
    content: string,
    signature?: string,
): boolean => {
    if (!signature) return false;
    return normalizeForComparison(content).includes(
        normalizeForComparison(signature),
    );
};

export const addSignature = (content: string, signature?: string): string => {
    if (
        !signature ||
        isEmptyHtml(content) ||
        containsSignature(content, signature)
    ) {
        return content;
    }

    const signatureParagraphs = signature
        .split('\n')
        .filter((line) => line.trim())
        .map((line) => `<p>${line.trim()}</p>`)
        .join('');

    return content + '<p><br></p>' + signatureParagraphs;
};

export const insertSignatureAtEnd = (quill: any, signature: string): void => {
    const currentLength = quill.getLength() - 1;

    quill.insertText(currentLength, '\n\n', 'silent');

    signature.split('\n').forEach((line, index) => {
        const trimmedLine = line.trim();
        if (trimmedLine) {
            if (index > 0) {
                quill.insertText(quill.getLength() - 1, '\n', 'silent');
            }
            quill.insertText(quill.getLength() - 1, trimmedLine, 'silent');
        }
    });
};
